import { Sparkles, ExternalLink, Mail, Phone } from "lucide-react";
import { parseAndValidateLinks } from "@/utils/linkValidator";

interface AIResponseProps {
  content: string;
  isLoading: boolean;
}

const formatAIResponse = (content: string) => {
  let processedContent = content;
  
  // First, auto-bold important Bio Beauté Zen keywords BEFORE processing links
  const importantKeywords = [
    'Bio Beauté Zen', 'Valérie', 'Valér-IA', 'rendez-vous', 'réservation',
    'soin', 'soins', 'massage', 'épilation', 'beauté', 'bio', 'naturel',
    'tarif', 'tarifs', 'prix', 'horaires', 'contact', 'téléphone',
    '04 99 53 83 08', 'biobeautezen.institut@gmail.com'
  ];
  
  importantKeywords.forEach(keyword => {
    const regex = new RegExp(`\\b(${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})\\b`, 'gi');
    processedContent = processedContent.replace(regex, (match) => {
      // Skip if already in markdown bold or HTML
      if (processedContent.includes(`**${match}**`) || 
          processedContent.includes(`<strong`) ||
          processedContent.includes(`[${match}]`) ||
          processedContent.includes(`>${match}<`)) {
        return match;
      }
      return `**${match}**`;
    });
  });
  
  // Then, process links to make them clickable (after bolding to avoid conflicts)
  processedContent = parseAndValidateLinks(processedContent);
  
  // Handle markdown-style formatting with better spacing
  processedContent = processedContent
    // Handle bold text **text**
    .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-bbz-text">$1</strong>')
    // Handle italic text *text*
    .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em class="italic">$1</em>')
    // Handle titles starting with ##
    .replace(/^## (.*$)/gm, '<h3 class="font-bold text-bbz-text text-lg mb-4 mt-6 first:mt-0">$1</h3>')
    // Handle numbered lists (1., 2., etc.)
    .replace(/^(\d+\.\s+)/gm, '<span class="font-bold text-bbz-green mr-2">$1</span>')
    // Handle bold titles followed by colon
    .replace(/<strong class="font-bold text-bbz-text">(.*?)<\/strong>\s*:/g, '<h4 class="font-bold text-bbz-text mb-3 mt-4 text-base">$1 :</h4>')
    // Better paragraph spacing - triple breaks for new sections
    .replace(/\n\n\n/g, '</div><div class="mb-6 mt-6">')
    // Double breaks for paragraphs  
    .replace(/\n\n/g, '</p><p class="mb-4 leading-relaxed">')
    // Single breaks become line breaks
    .replace(/\n/g, '<br class="mb-2"/>');

  // Better section handling with improved spacing
  const sections = processedContent.split('</div><div class="mb-6 mt-6">');
  
  return sections.map((section, sectionIndex) => {
    const paragraphs = section.split('</p><p class="mb-4 leading-relaxed">');
    
    return (
      <div key={sectionIndex} className="mb-6 last:mb-0">
        {paragraphs.map((paragraph, paragraphIndex) => {
          let cleanParagraph = paragraph.trim();
          
          // Handle lists - detect and format bullet points
          if (cleanParagraph.includes('<br class="mb-2"/>-') || cleanParagraph.includes('<br class="mb-2"/>•') || 
              cleanParagraph.startsWith('-') || cleanParagraph.startsWith('•')) {
            const listItems = cleanParagraph
              .split('<br class="mb-2"/>')
              .filter(item => item.trim())
              .map(item => item.trim());
            
            const hasListItems = listItems.some(item => item.startsWith('-') || item.startsWith('•'));
            
            if (hasListItems) {
              return (
                <div key={paragraphIndex} className="mb-5">
                  {listItems.map((item, itemIndex) => {
                    if (item.startsWith('-') || item.startsWith('•')) {
                      return (
                        <div key={itemIndex} className="flex items-start gap-3 mb-3 pl-2">
                          <span className="text-bbz-green font-bold mt-1 text-lg leading-none">•</span>
                          <div 
                            className="flex-1 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: item.substring(1).trim() }}
                          />
                        </div>
                      );
                    } else if (item.trim()) {
                      return (
                        <div 
                          key={itemIndex} 
                          className="mb-4 leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: item }}
                        />
                      );
                    }
                    return null;
                  })}
                </div>
              );
            }
          }
          
          // Regular formatted content with better spacing
          if (cleanParagraph.trim()) {
            return (
              <div 
                key={paragraphIndex} 
                className="mb-4 last:mb-0 leading-relaxed text-base"
                dangerouslySetInnerHTML={{ __html: cleanParagraph }}
              />
            );
          }
          
          return null;
        }).filter(Boolean)}
      </div>
    );
  }).filter(Boolean);
};

const LoadingIndicator = () => (
  <div className="space-y-6 py-8">
    {/* Avatar et titre animés */}
    <div className="flex items-center gap-4">
      <div className="relative flex-shrink-0 w-12 h-12 bg-gradient-to-br from-bbz-green to-bbz-green/80 rounded-full flex items-center justify-center animate-pulse-glow shadow-lg">
        <Sparkles className="w-6 h-6 text-white animate-pulse" />
        <div className="absolute inset-0 bg-gradient-to-br from-bbz-green/20 to-transparent rounded-full animate-shimmer bg-gradient-to-r from-transparent via-white/30 to-transparent bg-[length:200%_100%]"></div>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-bbz-text font-semibold text-lg">Valér-IA réfléchit</span>
        <div className="flex gap-1">
          <div className="w-2 h-2 bg-bbz-green rounded-full animate-typing-dots" style={{ animationDelay: '0ms' }}></div>
          <div className="w-2 h-2 bg-bbz-green rounded-full animate-typing-dots" style={{ animationDelay: '200ms' }}></div>
          <div className="w-2 h-2 bg-bbz-green rounded-full animate-typing-dots" style={{ animationDelay: '400ms' }}></div>
        </div>
      </div>
    </div>
    
    {/* Skeleton loader moderne */}
    <div className="space-y-4">
      <div className="h-4 bg-gradient-to-r from-bbz-green/10 via-bbz-green/20 to-bbz-green/10 rounded-lg animate-shimmer bg-[length:200%_100%]"></div>
      <div className="h-4 bg-gradient-to-r from-bbz-green/10 via-bbz-green/20 to-bbz-green/10 rounded-lg animate-shimmer bg-[length:200%_100%] w-3/4" style={{ animationDelay: '0.2s' }}></div>
      <div className="h-4 bg-gradient-to-r from-bbz-green/10 via-bbz-green/20 to-bbz-green/10 rounded-lg animate-shimmer bg-[length:200%_100%] w-1/2" style={{ animationDelay: '0.4s' }}></div>
    </div>
  </div>
);

const AIResponse = ({ content, isLoading }: AIResponseProps) => {
  if (isLoading) {
    return (
      <div className="bg-gradient-to-br from-white to-bbz-green/5 border border-bbz-green/20 rounded-xl p-6 md:p-8 shadow-lg shadow-bbz-green/10 animate-slide-up backdrop-blur-sm">
        <LoadingIndicator />
      </div>
    );
  }

  if (!content) return null;

  return (
    <div className="relative bg-gradient-to-br from-white via-white to-bbz-green/5 border border-bbz-green/20 rounded-xl p-6 md:p-8 shadow-lg shadow-bbz-green/10 animate-slide-up backdrop-blur-sm overflow-hidden">
      {/* Effet de brillance subtil */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-bbz-green/5 to-transparent opacity-50 animate-shimmer bg-[length:200%_100%] pointer-events-none"></div>
      
      <div className="relative z-10">
        <div className="flex items-start gap-4 mb-6 animate-content-reveal">
          <div className="relative flex-shrink-0 w-12 h-12 bg-gradient-to-br from-bbz-green to-bbz-green/80 rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-all duration-300 group">
            <Sparkles className="w-6 h-6 text-white group-hover:scale-110 transition-transform duration-300" />
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>
          <div className="animate-content-reveal" style={{ animationDelay: '0.1s' }}>
            <h3 className="text-xl font-semibold text-bbz-text mb-1">Réponse de Valér-IA</h3>
            <p className="text-bbz-text/60 text-sm">Voici ce que j'ai trouvé pour vous :</p>
          </div>
        </div>
        
        <div className="prose prose-lg max-w-none text-bbz-text ai-response-links animate-content-reveal" style={{ animationDelay: '0.2s' }}>
          {formatAIResponse(content)}
        </div>
      </div>
    </div>
  );
};

export default AIResponse;